import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./AccessibilityToolbar.css";

interface AccessibilityState {
  fontSize: number;
  grayscale: boolean;
  highContrast: boolean;
  invertedContrast: boolean;
  lightBackground: boolean;
  highlightLinks: boolean;
  readableFont: boolean;
}

const AccessibilityToolbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [positionY, setPositionY] = useState<number | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number>(0);
  const initialPositionY = useRef<number>(0);
  const hasMoved = useRef<boolean>(false);
  const [state, setState] = useState<AccessibilityState>({
    fontSize: 100,
    grayscale: false,
    highContrast: false,
    invertedContrast: false,
    lightBackground: false,
    highlightLinks: false,
    readableFont: false,
  });

  // טעינת העדפות מ-localStorage
  useEffect(() => {
    // שמירת הגודל הבסיסי לפני כל שינוי
    // נשתמש ב-setTimeout כדי לוודא שהדף נטען לגמרי
    const saveBaseFontSize = () => {
      const root = document.documentElement;
      const body = document.body;

      // נשמור את הגודל הבסיסי הנוכחי (לפני כל שינוי נגישות)
      if (!root.dataset.baseFontSize) {
        // נסיר זמנית הגדרות קודמות אם יש
        const tempRootFontSize = root.style.fontSize;
        const tempBodyFontSize = body.style.fontSize;
        root.style.removeProperty("font-size");
        body.style.removeProperty("font-size");

        // נבדוק את הגודל הבסיסי האמיתי
        const computedStyle = window.getComputedStyle(body);
        const currentSize = parseFloat(computedStyle.fontSize) || 16;
        root.dataset.baseFontSize = String(currentSize);

        // נחזיר את ההגדרות הקודמות אם היו
        if (tempRootFontSize) root.style.fontSize = tempRootFontSize;
        if (tempBodyFontSize) body.style.fontSize = tempBodyFontSize;
      }
    };

    // נשמור את הגודל הבסיסי מיד ואז נטען את ההעדפות
    saveBaseFontSize();

    const saved = localStorage.getItem("accessibility-preferences");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setState(parsed);
        // נשתמש ב-setTimeout קצר כדי לוודא שהגודל הבסיסי נשמר לפני החלת ההגדרות
        setTimeout(() => {
          applyAccessibilitySettings(parsed);
        }, 0);
      } catch (e) {
              }
    }

    // טעינת מיקום מ-localStorage
    const savedPosition = localStorage.getItem(
      "accessibility-toolbar-position"
    );
    if (savedPosition) {
      setPositionY(parseFloat(savedPosition));
    }
  }, []);

  // החלת הגדרות נגישות על הדף
  const applyAccessibilitySettings = (settings: AccessibilityState) => {
    const root = document.documentElement;
    const body = document.body;

    // גודל טקסט - נשתמש בגודל הבסיסי שנשמר ב-useEffect
    // אם עדיין לא נשמר (לא אמור לקרות), נשתמש בערך ברירת מחדל
    const baseSize = parseFloat(root.dataset.baseFontSize || "16");
    const multiplier = settings.fontSize / 100;
    const newSize = baseSize * multiplier;

    // נגדיר את הגודל החדש - על html נגדיר את הגודל המוחלט
    root.style.setProperty("font-size", `${newSize}px`, "important");
    body.style.setProperty("font-size", `${settings.fontSize}%`, "important");

    // נגדיר CSS variables לכל האלמנטים כדי שיגדלו יחסית
    root.style.setProperty(
      "--accessibility-font-multiplier",
      String(multiplier)
    );
    root.style.setProperty("--base-font-size", `${baseSize / 16}rem`); // המרה ל-rem

    // גווני אפור
    if (settings.grayscale) {
      root.style.filter = "grayscale(100%)";
    } else {
      root.style.filter = "";
    }

    // ניגודיות גבוהה
    if (settings.highContrast) {
      body.classList.add("high-contrast");
    } else {
      body.classList.remove("high-contrast");
    }

    // ניגודיות הפוכה
    if (settings.invertedContrast) {
      body.classList.add("inverted-contrast");
    } else {
      body.classList.remove("inverted-contrast");
    }

    // רקע בהיר
    if (settings.lightBackground) {
      body.classList.add("light-background");
    } else {
      body.classList.remove("light-background");
    }

    // הדגשת קישורים
    if (settings.highlightLinks) {
      body.classList.add("highlight-links");
    } else {
      body.classList.remove("highlight-links");
    }

    // פונט קריא
    if (settings.readableFont) {
      body.classList.add("readable-font");
    } else {
      body.classList.remove("readable-font");
    }
  };

  // עדכון הגדרה ושמירה
  const updateSetting = (key: keyof AccessibilityState, value: any) => {
    const newState = { ...state, [key]: value };
    setState(newState);
    applyAccessibilitySettings(newState);
    localStorage.setItem("accessibility-preferences", JSON.stringify(newState));
  };

  // עדכון פונט קריא בלבד (ללא שינוי גודל)
  const toggleReadableFont = () => {
    const newReadableFont = !state.readableFont;
    const newState = { ...state, readableFont: newReadableFont };
    setState(newState);

    // החלת רק את שינוי הפונט, ללא שינוי גודל
    const body = document.body;
    if (newReadableFont) {
      body.classList.add("readable-font");
    } else {
      body.classList.remove("readable-font");
    }

    localStorage.setItem("accessibility-preferences", JSON.stringify(newState));
  };

  // עדכון הדגשת קישורים בלבד (ללא שינוי גודל)
  const toggleHighlightLinks = () => {
    const newHighlightLinks = !state.highlightLinks;
    const newState = { ...state, highlightLinks: newHighlightLinks };
    setState(newState);

    // החלת רק את שינוי הדגשת הקישורים, ללא שינוי גודל
    const body = document.body;
    if (newHighlightLinks) {
      body.classList.add("highlight-links");
    } else {
      body.classList.remove("highlight-links");
    }

    localStorage.setItem("accessibility-preferences", JSON.stringify(newState));
  };

  // עדכון גווני אפור בלבד (ללא שינוי גודל)
  const toggleGrayscale = () => {
    const newGrayscale = !state.grayscale;
    const newState = { ...state, grayscale: newGrayscale };
    setState(newState);

    // החלת רק את שינוי גווני האפור, ללא שינוי גודל
    const root = document.documentElement;
    if (newGrayscale) {
      root.style.filter = "grayscale(100%)";
    } else {
      root.style.filter = "";
    }

    localStorage.setItem("accessibility-preferences", JSON.stringify(newState));
  };

  // עדכון ניגודיות גבוהה בלבד (ללא שינוי גודל)
  const toggleHighContrast = () => {
    const newHighContrast = !state.highContrast;
    const newState = { ...state, highContrast: newHighContrast };
    setState(newState);

    // החלת רק את שינוי הניגודיות, ללא שינוי גודל
    const body = document.body;
    if (newHighContrast) {
      body.classList.add("high-contrast");
    } else {
      body.classList.remove("high-contrast");
    }

    localStorage.setItem("accessibility-preferences", JSON.stringify(newState));
  };

  // עדכון ניגודיות הפוכה בלבד (ללא שינוי גודל)
  const toggleInvertedContrast = () => {
    const newInvertedContrast = !state.invertedContrast;
    const newState = { ...state, invertedContrast: newInvertedContrast };
    setState(newState);

    // החלת רק את שינוי הניגודיות ההפוכה, ללא שינוי גודל
    const body = document.body;
    if (newInvertedContrast) {
      body.classList.add("inverted-contrast");
    } else {
      body.classList.remove("inverted-contrast");
    }

    localStorage.setItem("accessibility-preferences", JSON.stringify(newState));
  };

  // עדכון רקע בהיר בלבד (ללא שינוי גודל)
  const toggleLightBackground = () => {
    const newLightBackground = !state.lightBackground;
    const newState = { ...state, lightBackground: newLightBackground };
    setState(newState);

    // החלת רק את שינוי הרקע, ללא שינוי גודל
    const body = document.body;
    if (newLightBackground) {
      body.classList.add("light-background");
    } else {
      body.classList.remove("light-background");
    }

    localStorage.setItem("accessibility-preferences", JSON.stringify(newState));
  };

  // הגדלת טקסט
  const increaseText = () => {
    const newSize = Math.min(state.fontSize + 10, 200);
    updateSetting("fontSize", newSize);
  };

  // הקטנת טקסט
  const decreaseText = () => {
    const newSize = Math.max(state.fontSize - 10, 50);
    updateSetting("fontSize", newSize);
  };

  // איפוס כל ההגדרות
  const reset = () => {
    const defaultState: AccessibilityState = {
      fontSize: 100,
      grayscale: false,
      highContrast: false,
      invertedContrast: false,
      lightBackground: false,
      highlightLinks: false,
      readableFont: false,
    };
    setState(defaultState);
    applyAccessibilitySettings(defaultState);
    localStorage.removeItem("accessibility-preferences");
    // איפוס גודל הטקסט למצב ברירת מחדל
    document.documentElement.style.removeProperty("font-size");
    document.body.style.removeProperty("font-size");
    document.documentElement.style.removeProperty(
      "--accessibility-font-multiplier"
    );
    document.documentElement.style.removeProperty("--base-font-size");
    // איפוס הגודל הבסיסי כדי שיישמר מחדש בפעם הבאה
    delete document.documentElement.dataset.baseFontSize;
  };

  // פונקציות גרירה - תמיכה גם במובייל
  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    // רק אם לוחצים על הכפתור עצמו
    if (toolbarRef.current) {
      hasMoved.current = false;
      setIsDragging(true);
      dragStartY.current = e.clientY;
      const rect = toolbarRef.current.getBoundingClientRect();
      initialPositionY.current = positionY !== null ? positionY : rect.top;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // פונקציה לטיפול במגע במובייל
  const handleTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
    if (toolbarRef.current) {
      hasMoved.current = false;
      setIsDragging(true);
      const touch = e.touches[0];
      dragStartY.current = touch.clientY;
      const rect = toolbarRef.current.getBoundingClientRect();
      initialPositionY.current = positionY !== null ? positionY : rect.top;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && toolbarRef.current) {
        const deltaY = e.clientY - dragStartY.current;

        // אם יש תזוזה משמעותית, זה גרירה
        if (Math.abs(deltaY) > 5) {
          hasMoved.current = true;
        }

        const newPosition = initialPositionY.current + deltaY;

        // הגבלת המיקום לגבולות המסך
        const minY = 0;
        const maxY = window.innerHeight - toolbarRef.current.offsetHeight;
        const clampedPosition = Math.max(minY, Math.min(maxY, newPosition));

        setPositionY(clampedPosition);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (isDragging && toolbarRef.current) {
        const touch = e.touches[0];
        const deltaY = touch.clientY - dragStartY.current;

        // אם יש תזוזה משמעותית, זה גרירה
        if (Math.abs(deltaY) > 5) {
          hasMoved.current = true;
        }

        const newPosition = initialPositionY.current + deltaY;

        // הגבלת המיקום לגבולות המסך
        const minY = 0;
        const maxY = window.innerHeight - toolbarRef.current.offsetHeight;
        const clampedPosition = Math.max(minY, Math.min(maxY, newPosition));

        setPositionY(clampedPosition);
        e.preventDefault(); // מניעת scroll בזמן גרירה
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
      }
    };

    const handleTouchEnd = () => {
      if (isDragging) {
        setIsDragging(false);
      }
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd);
      document.body.style.userSelect = "none"; // מניעת בחירת טקסט בזמן גרירה
      document.body.style.cursor = "grabbing";
      document.body.style.touchAction = "none"; // מניעת scroll במובייל
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      document.body.style.touchAction = "";
    };
  }, [isDragging, positionY]);

  // שמירת המיקום ב-localStorage כשהגרירה מסתיימת
  useEffect(() => {
    if (!isDragging && positionY !== null) {
      localStorage.setItem("accessibility-toolbar-position", String(positionY));
    }
  }, [isDragging, positionY]);

  const isRTL = i18n.language === "he";

  return (
    <div
      ref={toolbarRef}
      className={`accessibility-toolbar ${isRTL ? "rtl" : "ltr"} ${
        isDragging ? "dragging" : ""
      }`}
      style={
        positionY !== null
          ? { top: `${positionY}px`, transform: "none" }
          : undefined
      }
    >
      {/* כפתור פתיחה/סגירה */}
      <button
        className="accessibility-toolbar-toggle"
        onClick={(e) => {
          // רק אם לא גררנו (לא הייתה תזוזה משמעותית)
          if (!hasMoved.current) {
            setIsOpen(!isOpen);
          }
          hasMoved.current = false; // איפוס
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        aria-label={t("accessibilityTools")}
        aria-expanded={isOpen}
        type="button"
      >
        <span className="accessibility-icon" aria-hidden="true">
          ♿
        </span>
      </button>

      {/* תפריט נגישות */}
      {isOpen && (
        <div className="accessibility-menu">
          <div className="accessibility-menu-header">
            <h3 className="accessibility-menu-title">
              {t("accessibilityTools")}
            </h3>
            <button
              className="accessibility-menu-close"
              onClick={() => setIsOpen(false)}
              aria-label={t("close")}
              type="button"
            >
              ×
            </button>
          </div>

          <div className="accessibility-menu-content">
            {/* הגדלת טקסט */}
            <button
              className="accessibility-option"
              onClick={increaseText}
              aria-label={t("increaseText")}
              type="button"
            >
              <span className="accessibility-option-icon" aria-hidden="true">
                🔍+
              </span>
              <span className="accessibility-option-text">
                {t("increaseText")}
              </span>
            </button>

            {/* הקטנת טקסט */}
            <button
              className="accessibility-option"
              onClick={decreaseText}
              aria-label={t("decreaseText")}
              type="button"
            >
              <span className="accessibility-option-icon" aria-hidden="true">
                🔍-
              </span>
              <span className="accessibility-option-text">
                {t("decreaseText")}
              </span>
            </button>

            {/* גווני אפור */}
            <button
              className={`accessibility-option ${
                state.grayscale ? "active" : ""
              }`}
              onClick={toggleGrayscale}
              aria-label={t("grayscale")}
              aria-pressed={state.grayscale}
              type="button"
            >
              <span className="accessibility-option-icon" aria-hidden="true">
                ⬛
              </span>
              <span className="accessibility-option-text">
                {t("grayscale")}
              </span>
            </button>

            {/* ניגודיות גבוהה */}
            <button
              className={`accessibility-option ${
                state.highContrast ? "active" : ""
              }`}
              onClick={toggleHighContrast}
              aria-label={t("highContrast")}
              aria-pressed={state.highContrast}
              type="button"
            >
              <span className="accessibility-option-icon" aria-hidden="true">
                👁️
              </span>
              <span className="accessibility-option-text">
                {t("highContrast")}
              </span>
            </button>

            {/* ניגודיות הפוכה */}
            <button
              className={`accessibility-option ${
                state.invertedContrast ? "active" : ""
              }`}
              onClick={toggleInvertedContrast}
              aria-label={t("invertedContrast")}
              aria-pressed={state.invertedContrast}
              type="button"
            >
              <span className="accessibility-option-icon" aria-hidden="true">
                🔄
              </span>
              <span className="accessibility-option-text">
                {t("invertedContrast")}
              </span>
            </button>

            {/* רקע בהיר */}
            <button
              className={`accessibility-option ${
                state.lightBackground ? "active" : ""
              }`}
              onClick={toggleLightBackground}
              aria-label={t("lightBackground")}
              aria-pressed={state.lightBackground}
              type="button"
            >
              <span className="accessibility-option-icon" aria-hidden="true">
                💡
              </span>
              <span className="accessibility-option-text">
                {t("lightBackground")}
              </span>
            </button>

            {/* הדגשת קישורים */}
            <button
              className={`accessibility-option ${
                state.highlightLinks ? "active" : ""
              }`}
              onClick={toggleHighlightLinks}
              aria-label={t("highlightLinks")}
              aria-pressed={state.highlightLinks}
              type="button"
            >
              <span className="accessibility-option-icon" aria-hidden="true">
                🔗
              </span>
              <span className="accessibility-option-text">
                {t("highlightLinks")}
              </span>
            </button>

            {/* פונט קריא */}
            <button
              className={`accessibility-option ${
                state.readableFont ? "active" : ""
              }`}
              onClick={toggleReadableFont}
              aria-label={t("readableFont")}
              aria-pressed={state.readableFont}
              type="button"
            >
              <span className="accessibility-option-icon" aria-hidden="true">
                A
              </span>
              <span className="accessibility-option-text">
                {t("readableFont")}
              </span>
            </button>

            {/* איפוס */}
            <button
              className="accessibility-option reset"
              onClick={reset}
              aria-label={t("reset")}
              type="button"
            >
              <span className="accessibility-option-icon" aria-hidden="true">
                ↻
              </span>
              <span className="accessibility-option-text">{t("reset")}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessibilityToolbar;
